import {
  AppBar,
  IconButton,
  InputBase,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
  Grid,
  Link,
  CardContent,
  Card,
  Checkbox,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import CheckIcon from "@mui/icons-material/Check";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DescriptionIcon from "@mui/icons-material/Description";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import Lock from "@mui/icons-material/Lock";
import MinimizeIcon from "@mui/icons-material/Minimize";
import SearchIcon from "@mui/icons-material/Search";
import { CustomLinearProgress, HorizontalDataTable } from "@orbit/components";
import { APP_LEGGER_TYPES } from "app-constants";
import { StoresContext } from "contexts";
import { observer as hooksObserver } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { ROUTE_SEARCH_OWNER } from "routes/RouteList";
import { createCSVDownload } from "services/utils";
import { getUittrekselsEnabled } from "services/api";
import { PERCEEL_INFO_PANEL_ENUM, TOUR_ENUM } from "stores/UiStore";
import { useDebouncedCallback } from "use-debounce";
import { TableSubscriptionMessage } from "views/advanced-search/search-subscription-message";
import LongMenu from "./LongMenu";
import UittrekselDialog from "./UittrekselDialog";
import styles, { getMuiThemeEigenaars, getParcelInfoTableStyle } from "./PerceelInfoStyles";

const parsePeriodToReadableFormat = (period) => {
  if (period === "") {
    return period;
  }
  if (period.includes("ongoing")) {
    return "Huidige situatie";
  }
  const partitie = period.split("_");
  const parts = partitie[1].split("-");
  if (parts.length === 2) {
    return `${partitie[0]}: Vanaf ${parts[0]} tot ${parts[1]}`;
  }
  if (parts.length === 4) {
    if (parts[0].length === 4 && parts[1].length === 4) {
      return `${partitie[0]}: Vanaf ${parts[0]} tot ${parts[1]}-${parts[2]}-${parts[3]}`;
    }
    return `${partitie[0]}: Vanaf ${parts[0]}-${parts[1]}-${parts[2]} tot ${parts[3]}`;
  }
  if (parts.length === 6) {
    return `${partitie[0]}: Vanaf ${parts[0]}-${parts[1]}-${parts[2]} tot ${parts[3]}-${parts[4]}-${parts[5]}`;
  }
  return period;
};

/**
 * depending on the role users can view some tabs or not
 */
const roleRestrictions = [
  { role: "Administrator", restricted: [] },
  { role: "Openbaar-onderzoek", restricted: [] },
  { role: "Onteigening", restricted: [] },
  { role: "Planbaten", restricted: [] },
  { role: "Planschade", restricted: [] },
  { role: "Toekennen-premies-en-voordelen", restricted: [] },
  { role: "Heffen-gemeentelijke-belastingen", restricted: [] },
  { role: "Invorderingsprocedures", restricted: [] },
  { role: "Inventaris-onbebouwde-percelen", restricted: [2] },
  { role: "Inventaris-leegstand", restricted: [] },
  {
    role: "Bepaling-van-de-hoegrootheid-van-opcentiemen",
    restricted: [0, 1, 2],
  },
  { role: "Buitengewone-herziening-kis", restricted: [1] },
];

const DECEASED_COLOR = "#d32f2f";
const OWNER_INSZ_INDEX = 1;
const OWNER_VKBO_INDEX = 7;
const OWNER_ACTIONS_INDEX = 8;

const getCellPersonId = (cellValue) => {
  if (typeof cellValue === "string") return cellValue;
  if (React.isValidElement(cellValue)) {
    const children = cellValue?.props?.children;
    return typeof children === "string" ? children : "";
  }
  return "";
};

const parseFields = (data) => {
  return data.map((row) => {
    return row.map((value) => (Array.isArray(value) ? value.join(", ") : value));
  });
};

const NotAllowedRowRenderer = () => {
  return (
    <Tooltip title="Geen toegang tot gegevens o.b.v. huidig profiel, klik voor meer uitleg.">
      <Grid container direction="row" alignItems="center">
        <Grid item>
          <Lock fontSize="small" style={{ opacity: 0.5 }} />
        </Grid>
        <Grid item>
          <Link color="primary" href="https://orbitgis.atlassian.net/wiki/external/OGJiZGU0ODUwMDFlNGQ0YWExODYwYWI5MzE1MTQ4ZDc#Verschillende-profielen" target="_blank">
            {"Meer info"}
          </Link>
        </Grid>
      </Grid>
    </Tooltip>
  );
};

const PerceelInfo = hooksObserver(({ classes }) => {
  const {
    uiStore: { perceelInfoPanelHeight, perceelInfoTableHeight, setPerceelInfoPanel, perceelInfoPanel, popupModel, setShowTour, showTour },
    authStore: { role },
    routingStore: { push },
    applicationStore: {
      search: { activePerson: searchActivePerson },
      perceelInfoActief: {
        periodData,
        activePeriod,
        setActivePeriod,
        patrimonyParcels,
        owners,
        residents,
        vkbo,
        capakey,
        loading,
        loadResidents,
        loadVkbo,
        personalInfo,
        loadPersonalInfo,
        isPersonDeceased,
        deceasedPersonInsz: perceelInfoDeceasedInsz,
        deceasedOwnerInszList,
        setLoading,
      },
    },
  } = useContext(StoresContext);

  // Derive deceasedPersonInsz: sidebar activePerson -> perceelInfo state
  const deceasedPersonInsz = (searchActivePerson?.overleden ? searchActivePerson?.officialId : null) || perceelInfoDeceasedInsz;

  let { leggerType } = useParams();

  const [tabValue, setTabValue] = useState(0);
  const [uittrekselsEnabled, setUittrekselsEnabled] = useState(false);
  const [uittrekselDialogOpen, setUittrekselDialogOpen] = useState(false);
  const [houseHolder, setHouseHolders] = useState(false);
  const [busNumberFilter, setBusNumberFilter] = useState(true);
  const [searchValue, setSearchValue] = useState("");

  const [debouncedCallback] = useDebouncedCallback(
    // function
    (mySearchValue) => {
      downloadCSVObject[tabValue] ? downloadCSVObject[tabValue].setSearchValue(mySearchValue) : null;
    },
    // delay in ms
    200,
  );

  useEffect(() => {
    debouncedCallback(searchValue);
  }, [searchValue]);

  /**
   * when tabindex changes we remove the search value
   */
  useEffect(() => {
    setSearchValue("");
    setHouseHolders(false);
    setBusNumberFilter(true);
  }, [tabValue]);

  /**
   * when capakey changes we reset the tab index to default 0
   */
  useEffect(() => {
    setTabValue(0);
  }, [capakey]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (leggerType !== APP_LEGGER_TYPES.FISCAL) {
        setUittrekselsEnabled(false);
        return;
      }
      try {
        const result = await getUittrekselsEnabled();
        if (!cancelled) setUittrekselsEnabled(!!result?.enabled);
      } catch {
        if (!cancelled) setUittrekselsEnabled(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [leggerType]);

  // when the owners tabs is choosen then we need te fetch the data
  // when there is allready loading we should stop loading via the cancel
  useEffect(() => {
    const getResidentsForCapakey = async (capakey) => {
      if (tabValue === 2 && !getTabRestriction(role, 2)) {
        await loadResidents(capakey.full, popupModel.actuelePolygon, houseHolder, busNumberFilter);
      }
    };
    getResidentsForCapakey(capakey);
  }, [tabValue, capakey, houseHolder, busNumberFilter]);

  const parcelColumns = patrimonyParcels.rowNames.map((value) => {
    return {
      name: value.name,
      options: {
        customBodyRender: (value, { rowData }) => {
          if (value === "n.v.t.") {
            return <NotAllowedRowRenderer />;
          } else {
            return value;
          }
        },
      },
    };
  });

  const ownerColumns = owners.rowNames.map((rowName) => {
    if (rowName.name === "Acties") {
      return {
        name: "Acties",
        options: {
          customBodyRender: (value, { rowData }) => {
            if (Array.isArray(rowData)) {
              const personId = getCellPersonId(rowData[OWNER_INSZ_INDEX]);
              const vkbo = rowData[OWNER_VKBO_INDEX];
              return personId ? (
                <LongMenu
                  key={personId}
                  data-test-id="menu-actions-owner"
                  options={
                    vkbo === true
                      ? [
                          {
                            title: "Eigendommen opzoeken",
                            action: () => {
                              push(ROUTE_SEARCH_OWNER + leggerType + "/" + personId);
                            },
                          },
                          {
                            title: "VKBO",
                            action: async () => {
                              await loadVkbo(APP_LEGGER_TYPES.ACTUAL, personId);
                              setTabValue(3);
                              setLoading(false);
                            },
                          },
                        ]
                      : [
                          {
                            title: "Eigendommen opzoeken",
                            action: () => {
                              push(ROUTE_SEARCH_OWNER + leggerType + "/" + personId);
                            },
                          },
                          {
                            title: "Persoonsgegevens",
                            action: async () => {
                              await loadPersonalInfo(personId);
                              setTabValue(4);
                              setLoading(false);
                            },
                          },
                        ]
                  }
                />
              ) : null;
            }
          },
        },
      };
    } else if (rowName.name === "VKBO") {
      return {
        name: "VKBO",
        options: {
          customBodyRender: (value, { rowData }) => {
            if (value) {
              const [partial, insz] = rowData;
              return <CheckIcon />;
            }
          },
        },
      };
    } else if (rowName.name === "INSZ") {
      return {
        ...rowName,
        name: "INSZ of Ondernemingsnr.",
      };
    } else {
      return {
        name: rowName.name,
        options: {
          customBodyRender: (value, { rowData }) => {
            if (value === "n.v.t.") {
              return <NotAllowedRowRenderer />;
            } else {
              return value;
            }
          },
        },
      };
    }
  });

  // Wrap all cell values in red for deceased person directly in the data
  const persoonsgegevensData = (() => {
    if (!isPersonDeceased) return personalInfo.rowData;
    return personalInfo.rowData.map((row) => row.map((cell) => <span style={{ color: DECEASED_COLOR }}>{cell}</span>));
  })();

  const rowDataObject = {
    0: (mySearchValue = "") => patrimonyParcels.setSearchValue(mySearchValue),
    1: (mySearchValue = "") => owners.setSearchValue(mySearchValue),
    2: (mySearchValue = "") => residents.setSearchValue(mySearchValue),
    3: (mySearchValue = "") => vkbo.setSearchValue(mySearchValue),
    4: (mySearchValue = "") => personalInfo.setSearchValue(mySearchValue),
  };

  const downloadCSVObject = {
    0: patrimonyParcels,
    1: owners,
    2: residents,
    3: vkbo,
    4: personalInfo,
  };

  const changeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const getTabRestriction = (role, index) => {
    return roleRestrictions.find((roleRestriction) => roleRestriction.role.toLowerCase() === role.toLowerCase()).restricted.includes(index);
  };

  const options = {
    selectableRows: "none",
    download: false,
    print: false,
    filter: false,
    search: false,
    responsive: "standard", // scrollMaxHeight | scrollFullHeight | stacked
    sort: true,
    viewColumns: false,
    rowsPerPage: 25,
    tableBodyHeight: perceelInfoTableHeight,
    rowsPerPageOptions: [25, 50, 100],
    textLabels: {
      body: {
        noMatch: "Geen info gevonden...",
        toolTip: "Sorteren",
      },
      pagination: {
        rowsPerPage: "Rijen per pagina:",
        displayRows: "van",
      },
    },
  };

  // Wrap cell values in red for deceased owner rows directly in the data
  const eigenaarsData = (() => {
    const parsed = parseFields(owners.rowData);
    return parsed.map((row) => {
      // row[1] = INSZ (column order: Partitie, INSZ, Naam, Voornaam, Type, Rechten_Type, Rechten_Aandeel, VKBO, Acties)
      const personId = getCellPersonId(row[OWNER_INSZ_INDEX]);
      const isDeceased = !!personId && (personId === deceasedPersonInsz || deceasedOwnerInszList.includes(personId));

      if (isDeceased) {
        return row.map((cell, idx) => {
          // Skip VKBO (7) and Acties (8) - rendered as icons/menus
          if (idx === OWNER_VKBO_INDEX || idx === OWNER_ACTIONS_INDEX) return cell;
          return <span style={{ color: DECEASED_COLOR }}>{cell}</span>;
        });
      }
      return row;
    });
  })();

  return (
    <>
      <UittrekselDialog
        open={uittrekselDialogOpen}
        onClose={() => setUittrekselDialogOpen(false)}
        capakey={capakey?.full}
      />
      <div className={classes.title}>
        <AppBar position="static" color="secondary" elevation={0}>
          <CustomLinearProgress style={{ opacity: loading ? 1 : 0 }} variant="query" color={"secondary"} />
          <Toolbar>
            <div className={classes.title} style={{ display: "flex", alignItems: "center", minWidth: 0, gap: 8 }}>
              <Typography variant="h6" color="inherit" data-test-id="txt-capakey" id="txt-capakey" noWrap>
                Perceel {capakey.full}
              </Typography>
              {leggerType === APP_LEGGER_TYPES.FISCAL && uittrekselsEnabled && capakey?.full && (
                <Tooltip title="Fiscaal uittreksel (PDF)" aria-label="fiscaal uittreksel">
                  <IconButton
                    id="btn-uittreksel"
                    data-test-id="btn-uittreksel"
                    color="inherit"
                    onClick={() => setUittrekselDialogOpen(true)}
                    size="small"
                  >
                    <DescriptionIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            {(tabValue === 0 || tabValue === 1) && leggerType === APP_LEGGER_TYPES.ACTUAL && (
              <>
                <Typography variant="subtitle2">Periode:&nbsp;</Typography>
                <FormControl size="small" className={classes.periodeFormControl}>
                  <Select
                    variant="outlined"
                    data-test-id="tour-periode"
                    labelId="periode-label"
                    id="periode"
                    value={activePeriod}
                    onChange={(e) => setActivePeriod(e.target.value)}
                    className={classes.periodeSelect}
                  >
                    {/* I need to only get the periods from periodData and loop through them to create the menuItems */}
                    {periodData.length > 0 && periodData.map((period) => <MenuItem value={period.period}>{parsePeriodToReadableFormat(period.period)}</MenuItem>)}
                  </Select>
                </FormControl>
              </>
            )}
            <IconButton
              data-test-id="tour-parcelinfo"
              id="tour-parcelinfo"
              aria-label="Open tour"
              onClick={(e) => {
                if (showTour) {
                  setShowTour(false);
                } else {
                  setPerceelInfoPanel(PERCEEL_INFO_PANEL_ENUM.OPEN);
                  setShowTour(true, TOUR_ENUM.PERCEELINFO);
                }
              }}
            >
              <LiveHelpIcon style={{ color: "#ffffff" }} />
            </IconButton>
            <div className={classes.search} data-test-id="tour-search" id="tour-search">
              <div className={classes.searchIcon}>
                <SearchIcon />
              </div>
              <InputBase
                placeholder="Zoeken in tabel..."
                data-test-id="txt-area-search-tabel"
                value={searchValue}
                classes={{
                  root: classes.inputRoot,
                  input: classes.inputInput,
                }}
                inputProps={{ "aria-label": "search" }}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
            <Tooltip title="download als csv" aria-label="download als csv">
              <IconButton
                id="tour-download"
                data-test-id="tour-download"
                color="inherit"
                onClick={() => {
                  const { rowData, rowNames } = downloadCSVObject[tabValue];
                  createCSVDownload(rowNames, rowData, options);
                }}
              >
                <CloudDownloadIcon />
              </IconButton>
            </Tooltip>
            {perceelInfoPanel !== PERCEEL_INFO_PANEL_ENUM.MINIMIZED && (
              <Tooltip title="minimize" aria-label="minimize">
                <IconButton data-test-id="tour-view-minimize" id="tour-view" color="inherit" onClick={(e) => setPerceelInfoPanel(PERCEEL_INFO_PANEL_ENUM.MINIMIZED)}>
                  <MinimizeIcon style={{ paddingBottom: 10 }} />
                </IconButton>
              </Tooltip>
            )}
            {perceelInfoPanel !== PERCEEL_INFO_PANEL_ENUM.OPEN && (
              <Tooltip title="halfscreen" aria-label="open">
                <IconButton data-test-id="tour-view-halfscreen" id="tour-view" color="inherit" onClick={(e) => setPerceelInfoPanel(PERCEEL_INFO_PANEL_ENUM.OPEN)}>
                  <FullscreenExitIcon />
                </IconButton>
              </Tooltip>
            )}

            {perceelInfoPanel !== PERCEEL_INFO_PANEL_ENUM.OPEN_MAXIMIZED && (
              <Tooltip title="fullscreen" aria-label="open/maximize">
                <IconButton data-test-id="tour-view-fullscreen" id="tour-view" color="inherit" onClick={(e) => setPerceelInfoPanel(PERCEEL_INFO_PANEL_ENUM.OPEN_MAXIMIZED)}>
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            )}
          </Toolbar>
          <Toolbar className={classes.tabsToolbar}>
            <Tabs className={classes.tabs} value={tabValue} onChange={changeTab} color="inherit" variant="fullWidth">
              <Tab data-test-id="tour-table" id="tour-table" label="Patrimoniumpercelen" />
              <Tab label="Eigenaars" data-test-id="tbl-eigenaars" />
              <Tab label="Bewoners (actueel)" data-test-id="tbl-bewoners" />
              <Tab label="Vkbo (actueel)" disabled />
              <Tab label="Persoonsgegevens (actueel)" disabled />
            </Tabs>
          </Toolbar>
        </AppBar>

        {(tabValue === 0 || tabValue === 1) && <TableSubscriptionMessage type={leggerType} />}

        {(tabValue === 2 || tabValue === 3) && <TableSubscriptionMessage type={APP_LEGGER_TYPES.ACTUAL} />}

        <div
          style={{
            height: perceelInfoPanelHeight,
            overflow: "auto",
          }}
          id="parcel-table" /* mui-datatables style not in mui5 => public/table.css */
        >
          {!loading && (
            <>
              {tabValue === 0 && role && !getTabRestriction(role, 0) && (
                <HorizontalDataTable data={patrimonyParcels.rowData} columns={parcelColumns} options={options} muiTheme={getParcelInfoTableStyle} />
              )}
              {tabValue === 1 && role && !getTabRestriction(role, 1) && (
                <HorizontalDataTable
                  key={`eigenaars-${deceasedPersonInsz || "none"}-${(deceasedOwnerInszList || []).sort().join(",")}`}
                  data={eigenaarsData}
                  columns={ownerColumns}
                  options={options}
                  muiTheme={getMuiThemeEigenaars}
                />
              )}
              {tabValue === 2 && role && !getTabRestriction(role, 2) && (
                <HorizontalDataTable data={residents.rowData} columns={residents.rowNames} options={options} muiTheme={getParcelInfoTableStyle} />
              )}
              {tabValue === 3 && <HorizontalDataTable data={vkbo.rowData} columns={vkbo.rowNames} options={options} muiTheme={getParcelInfoTableStyle} />}
              {tabValue === 4 && <HorizontalDataTable data={persoonsgegevensData} columns={personalInfo.rowNames} options={options} muiTheme={getParcelInfoTableStyle} />}
              {role && getTabRestriction(role, tabValue) && (
                <Card style={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom align="center">
                      Geen toegang met dit profiel.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {tabValue === 2 && perceelInfoPanel !== PERCEEL_INFO_PANEL_ENUM.MINIMIZED && (
          <div className={classes.houseHolderWrapper}>
            <FormControlLabel
              className={classes.houseHolder}
              control={<Checkbox checked={houseHolder} onChange={(event) => setHouseHolders(event.target.checked)} name="houseHolder" />}
              label="Enkel gezinshoofden"
              data-test-id="btn-bewoners-gezinshoofden"
            />
            <FormControlLabel
              className={classes.busNumber}
              control={<Checkbox checked={busNumberFilter} onChange={(event) => setBusNumberFilter(event.target.checked)} name="busNumber" />}
              label="Filter op busnummers"
            />
          </div>
        )}
      </div>
    </>
  );
});

export default withStyles(styles)(PerceelInfo);
